// src/features/knowledge/knowledgeGraphApi.ts
// Cliente de API para o Grafo Neural 3D e Unificação da Base de Conhecimento

import { apiFetch } from '../../services/http';
import { getAuthHeaders } from '../../services/authHeaders';

export interface ConnectomeNode {
  id: string;
  label: string;
  description: string | null;
  nodeType: 'directive' | 'operational' | 'document' | 'concept';
  category: string | null;
  sourceId: string | null;
  sourceTable: string | null;
  temperature: number;
  temperatureTier: 'hot' | 'warm' | 'cold';
  degree: number;
  size: number;
  pos3d: { x: number; y: number; z: number };
  dynamics: {
    activityFrequency: number;
    activityAmplitude: number;
    pulseFrequency: number;
    glowIntensity: number;
  };
  visuals: {
    primaryColor: string;
    glowColor: string;
  };
  stats: {
    accessCount: number;
    validationCount: number;
    successRate: number;
    confidence: number;
    lastAccessedAt: number | null;
  };
}

export interface ConnectomeEdge {
  id: string;
  source: string;
  target: string;
  edgeType: string;
  weight: number;
  isCrossInfluence: boolean;
  transmissionSpeed: number;
  synapticIntensity: number;
  color: string;
  evidence: Record<string, any>;
}

export interface ConnectomeSummary {
  totalNodes: number;
  hotCount: number;
  warmCount: number;
  coldCount: number;
  avgTemperature: number;
  directiveCount: number;
  operationalCount: number;
  documentCount: number;
  totalEdges: number;
  crossInfluenceCount: number;
}

export interface ConnectomeData {
  nodes: ConnectomeNode[];
  edges: ConnectomeEdge[];
  summary: ConnectomeSummary;
  autoBootstrapped?: boolean;
}

export interface TemperatureLogEntry {
  id: string;
  nodeId: string;
  temperature: number;
  recencyScore: number;
  frequencyScore: number;
  validationScore: number;
  confidenceScore: number;
  computedAt: number;
}

/**
 * Carrega payload do conectoma 3D pronto para Three.js
 */
export async function fetch3DConnectome(token: string, filters?: {
  nodeType?: string;
  tier?: string;
  category?: string;
  search?: string;
}): Promise<ConnectomeData> {
  const params = new URLSearchParams();
  if (filters?.nodeType && filters.nodeType !== 'all') params.set('node_type', filters.nodeType);
  if (filters?.tier && filters.tier !== 'all') params.set('tier', filters.tier);
  if (filters?.category && filters.category !== 'all') params.set('category', filters.category);
  if (filters?.search) params.set('search', filters.search);

  const qs = params.toString() ? `?${params.toString()}` : '';
  return apiFetch<ConnectomeData>(`/knowledge/graph/3d-connectome${qs}`, {
    headers: getAuthHeaders(token),
  });
}

/**
 * Unifica e sincroniza as duas bases sob demanda
 */
export async function syncUnifiedKnowledge(token: string) {
  return apiFetch<{ ok: boolean; result: any }>('/knowledge/graph/sync-unified', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(token),
      'Content-Type': 'application/json',
    },
  });
}

/**
 * Força recálculo do decaimento de temperatura
 */
export async function decayTemperatures(token: string) {
  return apiFetch<{ ok: boolean; result: any }>('/knowledge/graph/temperature/decay', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(token),
      'Content-Type': 'application/json',
    },
  });
}

/**
 * Conecta dois nós criando uma aresta de influência ou suporte cruzado
 */
export async function createKnowledgeEdge(token: string, data: {
  sourceNodeId: string;
  targetNodeId: string;
  edgeType: string;
  weight?: number;
  evidence?: Record<string, any>;
}) {
  return apiFetch<{ item: any }>('/knowledge/graph/edges', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
}

/**
 * Aquece um nó (Touch) simulando acesso
 */
export async function touchKnowledgeNode(token: string, nodeId: string) {
  return apiFetch<{ ok: boolean }>(`/knowledge/graph/nodes/${nodeId}/touch`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(token),
      'Content-Type': 'application/json',
    },
  });
}

/**
 * Obtém histórico de temperatura de um nó
 */
export async function fetchTemperatureHistory(token: string, nodeId: string): Promise<TemperatureLogEntry[]> {
  const res = await apiFetch<{ items: TemperatureLogEntry[] }>(`/knowledge/graph/temperature/${nodeId}/history`, {
    headers: getAuthHeaders(token),
  });
  return res.items || [];
}

export interface ActivationStep {
  nodeId: string;
  activationOrder: number;
  relevanceScore: number;
  contributionType: 'directive' | 'solution' | 'evidence' | 'context';
  label?: string;
}

export interface ReflectionTrace {
  id: string;
  tenantId: string;
  actionType: string;
  actionSummary: string;
  activatedNodeIds: string[];
  activationPath: ActivationStep[];
  decisionConfidence: number;
  durationMs: number;
  outcomeSuccess?: number | null;
  outcomeFeedbackScore?: number | null;
  metadata: Record<string, any>;
  createdAt: number;
}

/**
 * Lista traces de reflexão neural da IA
 */
export async function fetchReflectionTraces(token: string, filters?: { limit?: number; actionType?: string }): Promise<ReflectionTrace[]> {
  const params = new URLSearchParams();
  if (filters?.limit) params.set('limit', String(filters.limit));
  if (filters?.actionType) params.set('action_type', filters.actionType);
  const qs = params.toString() ? `?${params.toString()}` : '';

  const res = await apiFetch<{ items: ReflectionTrace[] }>(`/knowledge/graph/reflection-traces${qs}`, {
    headers: getAuthHeaders(token),
  });
  return res.items || [];
}

/**
 * Polling do trace de reflexão mais recente após um determinado timestamp
 */
export async function fetchLiveReflectionTrace(token: string, afterTimestamp = 0): Promise<ReflectionTrace | null> {
  try {
    const res = await apiFetch<{ item: ReflectionTrace | null }>(`/knowledge/graph/reflection-traces/live?after=${afterTimestamp}`, {
      headers: getAuthHeaders(token),
    });
    return res.item || null;
  } catch {
    return null;
  }
}

/**
 * Detalhe de um trace de reflexão específico
 */
export async function fetchReflectionTraceDetail(token: string, traceId: string): Promise<ReflectionTrace | null> {
  const res = await apiFetch<{ item: ReflectionTrace }>(`/knowledge/graph/reflection-traces/${traceId}`, {
    headers: getAuthHeaders(token),
  });
  return res.item || null;
}

/**
 * Executa poda (pruning) manual do grafo de conhecimento
 */
export async function triggerKnowledgePruning(token: string): Promise<{ ok: boolean; results: any }> {
  return apiFetch<{ ok: boolean; results: any }>('/knowledge/graph/prune', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(token),
      'Content-Type': 'application/json',
    },
  });
}

/**
 * Registra feedback / reforço positivo do operador sobre a reflexão
 */
export async function recordReflectionFeedback(token: string, traceId: string, feedback: { success: boolean; score: number }) {
  return apiFetch<{ ok: boolean }>(`/knowledge/graph/reflection-traces/${traceId}/feedback`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(feedback),
  });
}
